import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, ArrowLeft, Upload, X, Image, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    icon: '',
    image: '',
    description: '',
    details: '',
    order: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPrograms();
  }, [navigate]);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/programs`);
      setPrograms(response.data);
    } catch (error) {
      toast.error("Failed to fetch programs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/uploads/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData({ ...formData, image: imageUrl });
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingProgram) {
        await axios.put(
          `${BACKEND_URL}/api/programs/${editingProgram.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Program updated successfully");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/programs`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Program created successfully");
      }
      setShowForm(false);
      setEditingProgram(null);
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save program");
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      title: program.title,
      icon: program.icon,
      image: program.image,
      description: program.description,
      details: program.details,
      order: program.order
    });
    setImagePreview(program.image || null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/programs/${programId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Program deleted successfully");
      fetchPrograms();
    } catch (error) {
      toast.error("Failed to delete program");
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      icon: '',
      image: '',
      description: '',
      details: '',
      order: 0
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingProgram(null);
    resetForm();
  };

  const togglePublish = async (program) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/programs/${program.id}`,
        { is_published: !program.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(program.is_published ? "Program hidden from website" : "Program published to website");
      fetchPrograms();
    } catch (error) {
      toast.error("Failed to update program visibility");
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {showForm ? (
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-programs-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Programs List
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {showForm ? (editingProgram ? `Edit: ${editingProgram.title}` : 'Add New Program') : 'Manage Programs'}
            </h1>
          </div>
          {!showForm && (
            <Button
              onClick={() => {
                setShowForm(true);
                setEditingProgram(null);
                resetForm();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-[#007A7A] hover:bg-[#007A7A]/90"
              data-testid="add-program-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Program
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="mb-8" data-testid="program-form">
            <CardHeader>
              <CardTitle>{editingProgram ? 'Edit Program' : 'Add New Program'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="program-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon *</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      required
                      placeholder="e.g., utensils, home, heart, scroll"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="program-icon-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Available: utensils, home, book-open, briefcase, heart, hand-helping, church, users, heart-pulse, trending-up, scroll, flower-2</p>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Program Image *</label>
                  
                  {/* Current/Preview Image */}
                  {(imagePreview || formData.image) && (
                    <div className="relative mb-4 inline-block">
                      <img
                        src={imagePreview || resolveImageUrl(formData.image)}
                        alt="Program preview"
                        className="w-64 h-40 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      className="hidden"
                      data-testid="program-image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="border-[#007A7A] text-[#007A7A]"
                    >
                      {isUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#007A7A] mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {formData.image ? 'Change Image' : 'Upload Image'}
                        </>
                      )}
                    </Button>
                    {!formData.image && !imagePreview && (
                      <span className="text-sm text-gray-500 flex items-center">
                        <Image className="w-4 h-4 mr-1" />
                        JPG, PNG, GIF, or WebP (max 5MB)
                      </span>
                    )}
                  </div>

                  {/* Fallback URL input for existing URLs */}
                  {formData.image && formData.image.startsWith('http') && !formData.image.includes(BACKEND_URL) && (
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ This image uses an external URL. Consider uploading a new image to ensure it remains available.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description * (shown on program cards)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="program-description-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Details * (shown in program popup)</label>
                  <textarea
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="program-details-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="program-order-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                <div className="flex space-x-4">
                  <Button 
                    type="submit" 
                    className="bg-[#007A7A] hover:bg-[#007A7A]/90" 
                    data-testid="save-program-btn"
                    disabled={!formData.image}
                  >
                    {editingProgram ? 'Update Program' : 'Create Program'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-program-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="programs-list">
            {programs.map((program) => (
              <Card key={program.id} className={`hover:shadow-lg transition-shadow duration-300 ${program.is_published === false ? 'opacity-60' : ''}`}>
                <div className="relative h-40 overflow-hidden rounded-t-lg">
                  <img
                    src={resolveImageUrl(program.image)}
                    alt={program.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=400';
                    }}
                  />
                  <div className="absolute top-2 left-2 flex gap-2">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      program.is_published === false 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-green-500 text-white'
                    }`}>
                      {program.is_published === false ? 'Hidden' : 'Published'}
                    </span>
                  </div>
                  <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    Order: {program.order}
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{program.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{program.description}</p>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => togglePublish(program)}
                      className={program.is_published === false ? 'border-green-600 text-green-600' : 'border-yellow-600 text-yellow-600'}
                      data-testid={`toggle-publish-${program.id}`}
                    >
                      {program.is_published === false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleEdit(program)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      data-testid={`edit-program-${program.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(program.id)}
                      className="border-red-600 text-red-600 hover:bg-red-50"
                      data-testid={`delete-program-${program.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrograms;
